// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { BootstrapImage, BootstrapLink } from '../../dsl/front';
import { AbstractModuleUi } from '../_abstract/ui';
import { prepareImage } from '../_filters/prepare-image';
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
let PartnersModuleUi = class PartnersModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        /**
         * Partners to render.
         */
        this.resolvedPartners = [];
        this.resolvedPartnersGrouped = {};
    }
    /**
     * @inheritDoc
     */
    async fetchRelated() {
        try {
            await this.resolvePartners();
        }
        catch (e) {
            this.onError(e);
        }
        this.resolvedPartners.length > 0 ? this.onReady() : this.onError();
    }
    /**
     *
     */
    isGrouped() {
        return this.module.content.grouped || true;
    }
    setResolvedPartnersGrouped() {
        const group = {};
        this.resolvedPartners.forEach(partner => {
            const labelKey = partner.label || '';
            if (!group[labelKey]) {
                group[labelKey] = [];
            }
            group[labelKey].push(partner);
        });
        this.resolvedPartnersGrouped = group;
    }
    /**
     * Determines whether the passed-in partner has its `label` field properly defined.
     *
     * @param partner - The partner which label is to be checked.
     */
    hasPartnerLabel(partner) {
        return typeof partner.label === 'string' && partner.label.length > 0;
    }
    /**
     * Resolves the partners' related data.
     */
    async resolvePartners() {
        const partnersToResolve = Array.isArray(this.module.content.partners) ? this.module.content.partners : [];
        await Promise.all(partnersToResolve.map(partner => {
            return new Promise((resolve, reject) => {
                this.resolveSinglePartner(partner)
                    .then(resolvedPartner => {
                    this.resolvedPartners = [...this.resolvedPartners, resolvedPartner];
                    this.setResolvedPartnersGrouped();
                    resolve();
                })
                    .catch(e => {
                    if (this.debug)
                        console.warn(e);
                    reject(e);
                });
            });
        }));
    }
    /**
     * Resolves the data of a single `Partner`.
     *
     * @param relatedPartner - The `Partner` which data is to be resolved.
     */
    async resolveSinglePartner(relatedPartner) {
        // Create the base object.
        const partner = {
            label: relatedPartner.label,
            hasExternalLink: relatedPartner.hasExternalLink,
            urlPath: relatedPartner.urlPath,
            images: [],
            scales: relatedPartner.images.map((image, idx) => {
                return relatedPartner.scales && relatedPartner.scales[idx] ? relatedPartner.scales[idx] : '1';
            })
        };
        // Wait for the `RelatedService` to resolve all the images.
        await Promise.all(relatedPartner.images.map(image => {
            return new Promise((resolve, reject) => {
                this.relatedService.resolve(image, {})
                    .then((resolvedImage) => {
                    const filteredImages = resolvedImage.srcset.filter((src) => src.height === null);
                    const resolvedImg = {
                        ...resolvedImage,
                        srcset: filteredImages
                    };
                    partner.images = [
                        ...partner.images,
                        prepareImage(resolvedImg, this.imageRatio, 300, this.module.isLazyLoaded)
                    ];
                    resolve();
                })
                    .catch(e => {
                    if (this.debug)
                        console.warn(e);
                    reject(e);
                });
            });
        }));
        // Return the populated object.
        return partner;
    }
};
PartnersModuleUi = __decorate([
    Component({
        name: 'PartnersModuleUi',
        components: {
            BootstrapImage,
            BootstrapLink
        },
        filters: { prepareImage },
        async created() {
            await this.fetchRelated();
        },
        template: `
    <section class="partners-module" :class="[debugClass, loadingClass, moduleColoredClass, getThemeByBgClass]"
             :style="{ backgroundColor: customColor }">
    <div class="container-fluid">
      <div class="row">
        <div class="col">
          <div v-if="isGrouped">
            <div class="partners-module__list list-unstyled m-0"  v-for="(partnerLabel, partnerIndex) in Object.keys(this.resolvedPartnersGrouped)" :key="partnerIndex">
              <span v-if="partnerLabel" class="partner__label">{{ partnerLabel }}</span>
              <div v-for="(partner, index) in resolvedPartnersGrouped[partnerLabel]" :key="index" class="partner">
                <template v-if="partner.hasExternalLink">
                  <BootstrapLink :link="{ target: partner.urlPath, newTab: true }" class="text-decoration-none">
                    <BootstrapImage v-for="(image, index) in partner.images" :key="index"
                                    v-bind="image" class="partner__image"
                                    :style="'max-height: unset; height: ' + partner.scales[index] * 1.7 + 'rem'" />
                  </BootstrapLink>
                </template>
                <template v-else>
                  <BootstrapImage v-for="(image, index) in partner.images" :key="index"
                                  v-bind="image" class="partner__image"
                                  :style="'max-height: unset; height: ' + partner.scales[index] * 1.7 + 'rem'" />
                </template>
              </div>
            </div>
          </div>
          <ul class="partners-module__list list-unstyled m-0" v-else>
            <li v-for="(partner, index) in resolvedPartners" :key="index" class="partner">
              <span v-if="hasPartnerLabel(partner)" class="partner__label">{{ partner.label }}</span>
              <template v-if="partner.hasExternalLink">
                <BootstrapLink :link="{ target: partner.urlPath, newTab: true }" class="text-decoration-none">
                  <BootstrapImage v-for="(image, index) in partner.images" :key="index"
                                  v-bind="image" class="partner__image"
                                  :style="'max-height: unset; height: ' + partner.scales[index] * 1.7 + 'rem'" />
                </BootstrapLink>
              </template>
              <template v-else>
                <BootstrapImage v-for="(image, index) in partner.images" :key="index"
                                v-bind="image" class="partner__image"
                                :style="'max-height: unset; height: ' + partner.scales[index] * 1.7 + 'rem'" />
              </template>
            </li>
          </ul>
        </div>
      </div>
    </div>
    </section>
  `
    })
], PartnersModuleUi);
export { PartnersModuleUi };
